import { format } from 'date-fns'
import { Bucket, CompletionResult, UsageTrend } from '@/types/dashboard'
import { ptBR } from 'date-fns/locale'

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat().format(num)
}

export const formatNumberWithCurrency = (num: number): string => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num)
}

export const formatDate = (timestamp: number): string => {
  return format(new Date(timestamp * 1000), 'MMM dd, yyyy', { locale: ptBR })
}

export const calculateTotalTokens = (results: CompletionResult[]): number => {
  return results.reduce((sum, result) => sum + result.input_tokens + result.output_tokens, 0)
}

export const calculateTotalRequests = (results: CompletionResult[]): number => {
  return results.reduce((sum, result) => sum + result.num_model_requests, 0)
}

export const calculateTotalInputTokens = (results: CompletionResult[]): number => {
  return results.reduce((sum, result) => sum + result.input_tokens, 0)
}

export const calculateTotalOutputTokens = (results: CompletionResult[]): number => {
  return results.reduce((sum, result) => sum + result.output_tokens, 0)
}

export const calculateTotalCachedTokens = (results: CompletionResult[]): number => {
  return results.reduce((sum, result) => sum + result.input_cached_tokens, 0)
}

export const calculateTotalsByDate = (buckets: Bucket[]): UsageTrend[] => {
  return buckets.map(bucket => {
    const inputTokens = calculateTotalInputTokens(bucket.results)
    const outputTokens = calculateTotalOutputTokens(bucket.results)

    return {
      date: formatDate(bucket.start_time),
      inputTokens,
      outputTokens,
      totalTokens: inputTokens + outputTokens,
      requests: calculateTotalRequests(bucket.results)
    }
  })
}

export const calculateOverallTotals = (buckets: Bucket[]) => {
  const allResults = buckets.flatMap(bucket => bucket.results)

  return {
    totalTokens: calculateTotalTokens(allResults),
    totalRequests: calculateTotalRequests(allResults),
    totalInputTokens: calculateTotalInputTokens(allResults),
    totalOutputTokens: calculateTotalOutputTokens(allResults),
    totalCachedTokens: calculateTotalCachedTokens(allResults)
  }
}

export const estimateCost = (inputTokens: number, outputTokens: number): number => {
  // Approximate cost calculation based on GPT-4 pricing
  // These values should be adjusted based on actual pricing
  const inputCostPer1K = 0.005
  const outputCostPer1K = 0.02

  return (inputTokens / 1000) * inputCostPer1K + (outputTokens / 1000) * outputCostPer1K
}
